import {
  endOfMonth,
  endOfToday,
  endOfWeek,
  endOfYear,
  endOfYesterday,
  parse,
  startOfMonth,
  startOfToday,
  startOfWeek,
  startOfYear,
  startOfYesterday,
  subDays,
  subHours,
  subMinutes,
  subMonths,
  subYears,
} from "date-fns";

export type DateRange = {
  label: string;
  value: {
    from: () => Date;
    to: () => Date;
    live: boolean;
  }
}

export const LAST_1_MINUTE: DateRange = {
  label: "Last 1 minute",
  value: {
    from: () => subMinutes(new Date(), 1),
    to: () => new Date(),
    live: true,
  },
};

export const LAST_5_MINUTES: DateRange = {
  label: "Last 5 minutes",
  value: {
    from: () => subMinutes(new Date(), 5),
    to: () => new Date(),
    live: true,
  },
};

export const LAST_15_MINUTES: DateRange = {
  label: "Last 15 minutes",
  value: {
    from: () => subMinutes(new Date(), 15),
    to: () => new Date(),
    live: true,
  },
};

export const LAST_30_MINUTES: DateRange = {
  label: "Last 30 minutes",
  value: {
    from: () => subMinutes(new Date(), 30),
    to: () => new Date(),
    live: true,
  },
};
export const LAST_1_HOUR: DateRange = {
  label: "Last 1 hour",
  value: {
    from: () => subHours(new Date(), 1),
    to: () => new Date(),
    live: true,
  },
};
export const LAST_3_HOURS: DateRange = {
  label: "Last 3 hours",
  value: {
    from: () => subHours(new Date(), 3),
    to: () => new Date(),
    live: true,
  },
};
export const LAST_6_HOURS: DateRange = {
  label: "Last 6 hours",
  value: {
    from: () => subHours(new Date(), 6),
    to: () => new Date(),
    live: true,
  },
};
export const LAST_12_HOURS: DateRange = {
  label: "Last 12 hours",
  value: {
    from: () => subHours(new Date(), 12),
    to: () => new Date(),
    live: true,
  },
};
export const LAST_24_HOURS: DateRange = {
  label: "Last 24 hours",
  value: {
    from: () => subHours(new Date(), 24),
    to: () => new Date(),
    live: true,
  },
};
export const LAST_2_DAYS: DateRange = {
  label: "Last 2 days",
  value: {
    from: () => subDays(new Date(), 2),
    to: () => new Date(),
    live: true,
  },
};
export const LAST_7_DAYS: DateRange = {
  label: "Last 7 days",
  value: {
    from: () => subDays(new Date(), 7),
    to: () => new Date(),
    live: true,
  },
};
export const LAST_30_DAYS: DateRange = {
  label: "Last 30 days",
  value: {
    from: () => subDays(new Date(), 30),
    to: () => new Date(),
    live: true,
  },
};
export const LAST_90_DAYS: DateRange = {
  label: "Last 90 days",
  value: {
    from: () => subDays(new Date(), 90),
    to: () => new Date(),
    live: true,
  },
};
export const LAST_6_MONTHS: DateRange = {
  label: "Last 6 months",
  value: {
    from: () => subMonths(new Date(), 6),
    to: () => new Date(),
    live: true,
  },
};
export const LAST_1_YEAR: DateRange = {
  label: "Last 1 year",
  value: {
    from: () => subYears(new Date(), 1),
    to: () => new Date(),
    live: true,
  },
};
export const LAST_2_YEARS: DateRange = {
  label: "Last 2 years",
  value: {
    from: () => subYears(new Date(), 2),
    to: () => new Date(),
    live: true,
  },
};
export const LAST_5_YEARS: DateRange = {
  label: "Last 5 years",
  value: {
    from: () => subYears(new Date(), 5),
    to: () => new Date(),
    live: true,
  },
};
export const YESTERDAY: DateRange = {
  label: "Yesterday",
  value: {
    from: () => startOfYesterday(),
    to: () => endOfYesterday(),
    live: false,
  },
};
export const DAY_BEFORE_YESTERDAY: DateRange = {
  label: "Day before yesterday",
  value: {
    from: () => subDays(startOfYesterday(), 1),
    to: () => subDays(endOfYesterday(), 1),
    live: false,
  },
};
export const THIS_DAY_LAST_WEEK: DateRange = {
  label: "This day last week",
  value: {
    from: () => subDays(startOfToday(), 7),
    to: () => subDays(endOfToday(), 7),
    live: false,
  },
};
export const PREVIOUS_WEEK: DateRange = {
  label: "Previous week",
  value: {
    from: () => subDays(startOfWeek(new Date()), 7),
    to: () => subDays(endOfWeek(new Date()), 7),
    live: false,
  },
};
export const PREVIOUS_MONTH: DateRange = {
  label: "Previous month",
  value: {
    from: () => subMonths(startOfMonth(new Date()), 1),
    to: () => subMonths(endOfMonth(new Date()), 1),
    live: false,
  },
};
export const PREVIOUS_2_MONTHS: DateRange = {
  label: "Previous 2 months",
  value: {
    from: () => subMonths(startOfMonth(new Date()), 2),
    to: () => new Date(),
    live: false,
  },
};
export const SINCE_DECEMBER_1_2023: DateRange = {
  label: "Since December 1, 2023",
  value: {
    from: () => new Date('2023-12-01T00:00:00'),
    to: () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
    live: false,
  },
};
export const PREVIOUS_YEAR: DateRange = {
  label: "Previous year",
  value: {
    from: () => subYears(startOfYear(new Date()), 1),
    to: () => subYears(endOfYear(new Date()), 1),
    live: false,
  },
};
export const TODAY: DateRange = {
  label: "Today",
  value: {
    from: () => startOfToday(),
    to: () => new Date(),
    live: true,
  },
};
export const THIS_WEEK_SO_FAR: DateRange = {
  label: "This week so far",
  value: {
    from: () => startOfWeek(new Date()),
    to: () => new Date(),
    live: true,
  },
};
export const THIS_MONTH_SO_FAR: DateRange = {
  label: "This month so far",
  value: {
    from: () => startOfMonth(new Date()),
    to: () => new Date(),
    live: true,
  },
};
export const THIS_YEAR_SO_FAR: DateRange = {
  label: "This year so far",
  value: {
    from: () => startOfYear(new Date()),
    to: () => new Date(),
    live: true,
  },
};

export const QUICK_RANGES = [
  LAST_5_MINUTES,
  LAST_15_MINUTES,
  LAST_30_MINUTES,
  LAST_1_HOUR,
  LAST_3_HOURS,
  LAST_6_HOURS,
  LAST_12_HOURS,
  LAST_24_HOURS,
  LAST_2_DAYS,
  LAST_7_DAYS,
  LAST_30_DAYS,
  LAST_90_DAYS,
  LAST_6_MONTHS,
  LAST_1_YEAR,
  LAST_2_YEARS,
  LAST_5_YEARS,
  YESTERDAY,
  DAY_BEFORE_YESTERDAY,
  THIS_DAY_LAST_WEEK,
  PREVIOUS_WEEK,
  PREVIOUS_MONTH,
  PREVIOUS_2_MONTHS,
  SINCE_DECEMBER_1_2023,
  PREVIOUS_YEAR,
  TODAY,
  THIS_WEEK_SO_FAR,
  THIS_MONTH_SO_FAR,
  THIS_YEAR_SO_FAR,
];

export const DEFAULT_QUICK_RANGE = LAST_24_HOURS

export const DEFAULT_OVERVIEW_RANGE = SINCE_DECEMBER_1_2023

export const CUSTOM_DATE_RANGE_LABEL_FORMAT = "LLL dd, y";

export const dateRangeFromString = (paramValue: string | null): DateRange | undefined => {
  if (paramValue) {
    const matchingQuickRange = QUICK_RANGES.find((_dateRange) => _dateRange.label === paramValue)

    if (matchingQuickRange) {
      return matchingQuickRange
    } else if (paramValue.includes(" - ")) {
      const [from, to] = paramValue.split(" - ");
      return {
        label: paramValue,
        value: {
          from: () => parse(from, CUSTOM_DATE_RANGE_LABEL_FORMAT, new Date()),
          to: () => parse(to, CUSTOM_DATE_RANGE_LABEL_FORMAT, new Date()),
          live: false
        }
      }
    }
  }
}
